// D16a - pure helpers linking a forests card to its project outline(s).
// No shared key exists between forest_group_summary (cards, keyed by title)
// and the outline layers (tree_project_extents keyed by farmer_group; the
// Mizan zone layers keyed by nothing), so the link is geometric: a card
// matches a set of outlines when any one of them contains the card's
// centroid. Pure functions, unit-tested; the map call stays in the
// component (§7).

type OutlineFeature = {
  geometry: { type: string; coordinates: unknown } | null;
};

type Bounds = [[number, number], [number, number]]; // [[minLng, minLat], [maxLng, maxLat]]

// Walk arbitrarily nested coordinate arrays and fold every [lng, lat, ...]
// position into the bbox. Tolerates 3-element positions (the Mizan zone
// layers serialise elevation as a third value).
const foldPositions = (
  node: unknown,
  acc: { minLng: number; minLat: number; maxLng: number; maxLat: number },
): void => {
  if (!Array.isArray(node)) return;
  if (typeof node[0] === 'number' && typeof node[1] === 'number') {
    const lng = node[0];
    const lat = node[1];
    if (lng < acc.minLng) acc.minLng = lng;
    if (lng > acc.maxLng) acc.maxLng = lng;
    if (lat < acc.minLat) acc.minLat = lat;
    if (lat > acc.maxLat) acc.maxLat = lat;
    return;
  }
  for (const child of node) foldPositions(child, acc);
};

export const bboxOfFeature = (feature: OutlineFeature): Bounds | null => {
  if (!feature.geometry) return null;
  const acc = { minLng: Infinity, minLat: Infinity, maxLng: -Infinity, maxLat: -Infinity };
  foldPositions(feature.geometry.coordinates, acc);
  if (acc.minLng === Infinity) return null;
  return [
    [acc.minLng, acc.minLat],
    [acc.maxLng, acc.maxLat],
  ];
};

/**
 * Bounds for a card, or null when it has no match (caller falls back to the
 * pre-existing fly-to behaviour). Matching is per-outline (does THIS outline's
 * own bbox contain the card's centroid?) - that stays the discriminator, so a
 * future project's own outline set still only matches its own card. But once
 * any outline in the set matches, the returned bounds union EVERY outline
 * passed in, not just the one(s) that matched: `outlines` is always one
 * project's full outline family (e.g. mizan1zones' 12 grid cells subdividing
 * one farm, plus that project's tree_project_extents row), never a mix of
 * unrelated projects, so the whole family belongs together on a match
 * (founder correction 2026-09-18 - matching against a single grid cell had
 * fit the map to a slice of the farm instead of the whole thing).
 */
export const boundsOfOutlinesContaining = (
  point: [number, number] | null,
  outlines: OutlineFeature[] | undefined,
): Bounds | null => {
  if (!point || !outlines?.length) return null;
  const [lng, lat] = point;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;

  const hasMatch = outlines.some((feature) => {
    const box = bboxOfFeature(feature);
    if (!box) return false;
    return lng >= box[0][0] && lng <= box[1][0] && lat >= box[0][1] && lat <= box[1][1];
  });
  if (!hasMatch) return null;

  return boundsOfOutlines(outlines);
};

/**
 * Union bounds of every outline given, or null when none has geometry.
 * Used since 2026-09-17 (forest map scoped to Mizan): every outline on the map
 * is a Mizan one, so the forests view always fits to the whole set.
 */
export const boundsOfOutlines = (outlines: OutlineFeature[] | undefined): Bounds | null => {
  let merged: Bounds | null = null;
  for (const feature of outlines ?? []) {
    const box = bboxOfFeature(feature);
    if (!box) continue;
    merged = merged
      ? [
          [Math.min(merged[0][0], box[0][0]), Math.min(merged[0][1], box[0][1])],
          [Math.max(merged[1][0], box[1][0]), Math.max(merged[1][1], box[1][1])],
        ]
      : box;
  }
  return merged;
};

/**
 * fitBounds padding for the forests view: ~40px of air (founder spec) measured
 * from the overlays that cover the map - tab bar + stat block on top, project
 * cards at the bottom - not from the raw canvas edge, or the outlines land
 * underneath them. Overlay heights measured from the 2026-09-17 screenshots.
 */
export const forestFitPadding = (viewportWidth: number) =>
  viewportWidth < 768
    ? { top: 280, bottom: 370, left: 40, right: 40 } // bottom also clears the legend
    : { top: 210, bottom: 230, left: 40, right: 40 };

// Cap for the FORESTS opening / subcard fit (founder 2026-09-17). Legend clicks
// keep their own uncapped fit.
export const FOREST_OPENING_MAX_ZOOM = 14;

/**
 * Founder decision 2026-09-18: the Mizan zone + extent fit belongs to the
 * "100% Deforestation free" forests card, not to "Farm sequestration in
 * Mizan". That card's forest_group_summary row has NULL geometry, so the
 * geometric match above can never find it - it is matched explicitly here.
 *
 * Why the title and not the feature id: forest_group_summary is a PostGIS
 * view with no primary key, so GeoServer mints a fresh fid per request
 * (checked live 2026-09-18: two back-to-back GetFeature calls returned
 * `fid-..._-43fe` and then `fid-..._-43f9` for this same row). The title is
 * the only identifying column the view exposes, and it is already what the
 * app keys cards on (Cards.tsx `group: title`, `setGroup({ group: title })`).
 * Compared trimmed and case-insensitively so a casing edit upstream does not
 * silently drop the fit. If Fairchain renames the card, the match fails
 * closed: the click falls back to onFlyToTrees, nothing wrong is drawn.
 */
export const MIZAN_ZONE_CARD_TITLE = '100% Deforestation free';

export const isMizanZoneCard = (title: string | null | undefined): boolean =>
  (title ?? '').trim().toLowerCase() === MIZAN_ZONE_CARD_TITLE.toLowerCase();

/**
 * Founder decision 2026-09-18 (fly-to Question): the tree-pin cards fly to
 * the pins of THEIR project, not to the centre of every drawn pin (which,
 * once pins existed in Limu, BAC and Mizan, was empty ground between the
 * clusters). Card title -> all_trees_combined.farmer_group, founder-supplied,
 * not inferred. Same title matching as isMizanZoneCard, same reason (no
 * stable id on forest_group_summary). A card absent here (Biocompost in
 * Kericho - no trees) gets undefined and keeps the unfiltered fallback.
 */
export const TREE_CARD_PROJECTS: Record<string, string> = {
  'trees, ethiopia': 'Limu Outgrowers',
  'farm sequestration in mizan, ethiopia': 'Mizan 1 Ingrowers',
};

export const treeProjectForCard = (title: string | null | undefined): string | undefined =>
  TREE_CARD_PROJECTS[(title ?? '').trim().toLowerCase()];

/**
 * Founder decision 2026-09-20 (staging review): the "Trees, Ethiopia" card
 * lands exactly where "Farm sequestration in Mizan, Ethiopia" lands - same
 * project pins, same anchor, same fit. This OVERRIDES the 2026-09-19
 * per-project 100 km rule for that one card only: its own project (Limu
 * Outgrowers) and its own coordinates are no longer used for the landing.
 * Every project's pins still draw; only where the map goes changes. Matched
 * by title, same reason as isMizanZoneCard (no stable id on
 * forest_group_summary). A card not listed here lands on itself as before.
 */
export const LANDING_CARD_ALIAS: Record<string, string> = {
  'trees, ethiopia': 'farm sequestration in mizan, ethiopia',
};

export const landingCardTitle = (title: string | null | undefined): string => {
  const key = (title ?? '').trim().toLowerCase();
  return LANDING_CARD_ALIAS[key] ?? key;
};

// Founder decision 2026-09-19: a tree-pin card flies to its project's pins
// that lie within 100 km of the card's own coordinates - the same rule the
// tree_project_extents view uses - so the Mizan-located "Limu Outgrowers"
// rows (Question 27) stop dragging the Limu landing into an empty valley.
export const TREE_PIN_ANCHOR_RADIUS_KM = 100;
// Cap for the (no-anchor) all-pins bbox fit (founder 2026-09-19): never closer
// than the old fly-to zoom. The anchored tree cards no longer fit a bbox - see
// LANDING_ZOOM below - but a card row with null geometry still fits every pin.
export const TREE_PIN_MAX_ZOOM = 17;
// No project pins within the radius (Biocompost in Kericho has no trees):
// land on the card itself at a town-scale zoom rather than somewhere empty.
export const TREE_CARD_FALLBACK_ZOOM = 12;

// Founder decision 2026-09-21 (map-ux-4, Question 35 = option a): both tree-pin
// cards ("Trees, Ethiopia" and "Farm sequestration in Mizan", which alias to the
// same Mizan project) land on the SAME densest pocket of the project's pins,
// computed live from the loaded pins (never hardcoded, so it tracks the data if
// Fairchain moves the planting) - but at DIFFERENT zooms straddling the dot->pin
// handoff (FOREST_PIN_MIN_ZOOM 16.5), so each shows a different face of the map:
//   - "Farm sequestration in Mizan" lands at 16.9 (above the handoff) -> tree PINS.
//   - "Trees, Ethiopia" lands at 16.25 (below the handoff) -> yellow DOTS, with the
//     green pins appearing on the first zoom in.
// This supersedes the 2026-09-19 bbox fit for the anchored cards.
export const LANDING_ZOOM = 16.9; // default / "Farm sequestration" -> pins
export const TREES_LANDING_ZOOM = 16.25; // "Trees, Ethiopia" -> yellow dots

// Per-card landing zoom, keyed by the card's OWN title (before the Mizan alias),
// so the two cards that share the Mizan pocket can still land at different zooms.
// A card not listed uses LANDING_ZOOM.
export const LANDING_ZOOM_BY_CARD: Record<string, number> = {
  'trees, ethiopia': TREES_LANDING_ZOOM,
};

export const landingZoomForCard = (title: string | null | undefined): number =>
  LANDING_ZOOM_BY_CARD[(title ?? '').trim().toLowerCase()] ?? LANDING_ZOOM;
// Grid cell used to find the busiest pocket. ~150 m is well inside a degree at
// the Mizan latitude; the exact size is not critical - it only buckets pins to
// find the densest neighbourhood, whose centroid becomes the landing centre.
export const DENSITY_CELL_METERS = 150;
const METERS_PER_DEG_LAT = 111_320;

/**
 * Centroid of the ~cellMeters grid cell holding the most points. Buckets each
 * point into a lat/lng cell (longitude step taken at the points' mean latitude
 * so the grid is uniform), finds the fullest cell (ties broken by the cell key
 * so the result is deterministic), and averages that cell's points. null when
 * there are no points. Pure so it can be unit-tested against the real fixtures.
 */
export const densestPoint = (
  points: [number, number][],
  cellMeters = DENSITY_CELL_METERS,
): [number, number] | null => {
  if (!points.length) return null;
  const latStep = cellMeters / METERS_PER_DEG_LAT;
  const meanLat = points.reduce((s, [, lat]) => s + lat, 0) / points.length;
  const lngStep = cellMeters / (METERS_PER_DEG_LAT * Math.cos((meanLat * Math.PI) / 180));
  const buckets: Record<string, [number, number][]> = {};
  for (const [lng, lat] of points) {
    const key = `${Math.floor(lng / lngStep)}:${Math.floor(lat / latStep)}`;
    (buckets[key] ??= []).push([lng, lat]);
  }
  let best: [number, number][] | null = null;
  let bestKey = '';
  for (const key of Object.keys(buckets)) {
    const cell = buckets[key];
    if (!best || cell.length > best.length || (cell.length === best.length && key < bestKey)) {
      best = cell;
      bestKey = key;
    }
  }
  if (!best) return null;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of best) {
    sx += x;
    sy += y;
  }
  return [sx / best.length, sy / best.length];
};

type PinLike = {
  geometry?: { coordinates?: unknown } | null;
  properties?: { farmer_group?: unknown } | null;
};

// Great-circle distance, good to well under 1% at this scale.
export const distanceKm = (a: [number, number], b: [number, number]): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

// Accepts a Point or a single-position MultiPoint (the Mizan rows arrive as
// MultiPoint; getTransformedData normally collapses them first, but the
// helper must not depend on that).
const pointOf = (pin: PinLike): [number, number] | null => {
  let c = pin.geometry?.coordinates;
  if (Array.isArray(c) && Array.isArray(c[0])) c = c[0];
  if (!Array.isArray(c) || typeof c[0] !== 'number' || typeof c[1] !== 'number') return null;
  return [c[0], c[1]];
};

export type TreeFlyTarget =
  // Founder 2026-09-21 (map-ux-4): an anchored tree card lands at LANDING_ZOOM
  // centred on the densest pocket of its project's in-range pins. `center` is
  // the centroid of the busiest ~150 m cell; `pinCount` = pins in range.
  | { kind: 'land'; center: [number, number]; zoom: number; pinCount: number }
  // No anchor (a card row with null geometry): fitBounds on every pin's bbox,
  // overlay-aware padding, capped at zoom 17. `pinCount` = how many defined it.
  | { kind: 'fit'; bounds: Bounds; pinCount: number }
  // No pins in range (Biocompost in Kericho): the card itself at zoom 12.
  | { kind: 'fly'; center: [number, number]; zoom: number; pinCount: 0 };

/**
 * Where a tree-pin card's click lands. Pure so it can be unit-tested against
 * the real fixtures and mirrored by the Playwright spec.
 * - anchor + farmerGroup: that project's pins within 100 km of the anchor ->
 *   land at `landingZoom` on the densest pocket of those pins (founder
 *   2026-09-21, map-ux-4); none in range -> the anchor at zoom 12.
 * - anchor, no farmerGroup (a card with no trees): the anchor at zoom 12.
 * - no anchor (a card row with null geometry): fit the bbox of every pin
 *   passed in (or null if there are none).
 *
 * `landingZoom` (default LANDING_ZOOM) lets the two Mizan-landing cards share the
 * densest pocket but arrive at different zooms - "Trees, Ethiopia" at 16.25 (on
 * dots), "Farm sequestration" at 16.9 (on pins). See landingZoomForCard.
 */
export const treeFlyTarget = (
  pins: PinLike[],
  farmerGroup: string | undefined,
  anchor: [number, number] | null,
  landingZoom: number = LANDING_ZOOM,
): TreeFlyTarget | null => {
  const chosen: [number, number][] = [];
  for (const pin of pins) {
    const p = pointOf(pin);
    if (!p) continue;
    if (anchor) {
      if (!farmerGroup || pin.properties?.farmer_group !== farmerGroup) continue;
      if (distanceKm(anchor, p) > TREE_PIN_ANCHOR_RADIUS_KM) continue;
    }
    chosen.push(p);
  }
  if (!chosen.length) {
    return anchor
      ? { kind: 'fly', center: anchor, zoom: TREE_CARD_FALLBACK_ZOOM, pinCount: 0 }
      : null;
  }
  // Anchored tree card (both real ones land on Mizan): centre on the densest
  // pocket of the project's in-range pins at the card's landing zoom, so the
  // visitor arrives inside the planting (founder 2026-09-21, map-ux-4).
  if (anchor) {
    return {
      kind: 'land',
      center: densestPoint(chosen)!, // chosen is non-empty here
      zoom: landingZoom,
      pinCount: chosen.length,
    };
  }
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  for (const [lng, lat] of chosen) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return {
    kind: 'fit',
    bounds: [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    pinCount: chosen.length,
  };
};
